import React, { useEffect, useId, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import MaterialIcon from "../material-icon";
import { PrimaryButton, TextButton, reducedMotion, riseIn, sp } from "../support-ui/support-ui";
import { tStr } from "../../utils/i18n-helpers";
import { isPublicPath } from "../../utils/publicPaths";
import useCookieConsentStore, {
    ALL_CONSENT,
    CONSENT_CATEGORIES,
    ConsentCategory,
    ConsentChoice,
    NO_CONSENT,
    consentApplies,
} from "../../stores/cookieConsentStore";

const Card = styled.div`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 10001; /* above the chat widget's button */
    box-sizing: border-box;
    width: 380px;
    max-width: calc(100vw - 48px);
    max-height: calc(100dvh - 48px);
    overflow-y: auto;
    padding: 20px;
    border-radius: 14px;
    border: 1px solid color-mix(in srgb, ${sp.ink} 10%, transparent);
    background: color-mix(in srgb, ${sp.surface} 95%, transparent);
    backdrop-filter: blur(16px) saturate(1.4);
    -webkit-backdrop-filter: blur(16px) saturate(1.4);
    box-shadow:
        0 24px 64px -16px rgba(15, 28, 77, 0.28),
        0 2px 6px rgba(15, 28, 77, 0.06);
    font-family: ${sp.body};
    color: ${sp.ink};
    animation: ${riseIn} ${sp.dIn} ${sp.easeOut};
    ${reducedMotion(css`animation: none;`)}

    @media (max-width: 560px) {
        left: 16px;
        right: 16px;
        bottom: calc(16px + env(safe-area-inset-bottom));
        width: auto;
        max-width: none;
        max-height: calc(100dvh - 32px - env(safe-area-inset-bottom));
    }

    @media (prefers-reduced-transparency: reduce) {
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        background: ${sp.surface};
    }
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
`;

const CookieDisc = styled.span`
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${sp.accent};
    background: color-mix(in srgb, ${sp.accent} 10%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, ${sp.accent} 30%, transparent);
`;

const Title = styled.h2`
    flex: 1;
    margin: 0;
    font-family: ${sp.display};
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${sp.ink};
`;

const Text = styled.p`
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 1.6;
    color: ${sp.muted};

    a {
        color: ${sp.ink2};
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    a:hover {
        color: ${sp.accent};
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CustomizeButton = styled(TextButton)`
    align-self: center;
`;

const Button = styled(PrimaryButton)`
    width: 100%;
    height: 40px;
    font-size: 14px;
`;

const OutlineButton = styled(Button)<{ $accent?: boolean }>`
    background: transparent;
    box-shadow: inset 0 0 0 1px ${({ $accent }) => ($accent ? sp.accent : sp.borderStrong)};
    color: ${({ $accent }) => ($accent ? sp.accent : sp.ink2)};
    transition: box-shadow ${sp.t}, color ${sp.t}, background ${sp.t};

    &:hover {
        background: ${({ $accent }) => ($accent ? sp.accentWash : "transparent")};
        box-shadow: inset 0 0 0 1px ${sp.accent};
        color: ${sp.accent};
    }
`;

const CloseButton = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    width: 32px;
    height: 32px;
    margin: -6px -6px 0 0;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${sp.muted};
    transition: background ${sp.t}, color ${sp.t};

    &:hover {
        background: ${sp.sunken};
        color: ${sp.ink};
    }

    &:focus-visible {
        outline: 2px solid ${sp.accent};
        outline-offset: 1px;
    }
`;

const Rows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${sp.borderStrong};
`;

const RowText = styled.div`
    flex: 1;
    min-width: 0;

    b {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: ${sp.ink};
    }

    span {
        display: block;
        margin-top: 2px;
        font-size: 11px;
        line-height: 1.5;
        color: ${sp.muted};
    }
`;

const RowControl = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
`;

const AlwaysOn = styled.small`
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${sp.faint};
`;

const Switch = styled.button<{ $on: boolean }>`
    all: unset;
    box-sizing: border-box;
    position: relative;
    width: 36px;
    height: 20px;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ $on }) => ($on ? sp.accent : "#C9CED8")};
    transition: background ${sp.t};

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 999px;
        background: #fff;
        box-shadow: 0 1px 2px rgba(15, 28, 77, 0.25);
        transform: translateX(${({ $on }) => ($on ? "16px" : "0")});
        transition: transform ${sp.t} ${sp.easeOut};
    }

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    &:focus-visible {
        outline: 2px solid ${sp.accent};
        outline-offset: 2px;
    }
`;

/**
 * Asks for consent on the public website until the visitor decides, and
 * reopens as the settings from the "Cookie settings" links anywhere. Inside
 * the portal it does not interrupt: without a decision the statistics simply
 * stay off. Nothing is preselected and rejecting takes one click, like accepting.
 */
// The public pages are prerendered to static HTML at build time, where nobody
// has decided yet. Rendered there, the banner sat in every visitor's first paint
// until React took over and removed it again.
const isPrerender = (): boolean =>
    typeof navigator !== "undefined" && navigator.userAgent.includes("ManagdPrerender");

const CookieBanner: React.FC = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { choice, settingsOpen, save, closeSettings } = useCookieConsentStore();
    const [view, setView] = useState<"main" | "settings">("main");
    const [draft, setDraft] = useState<ConsentChoice>(NO_CONSENT);
    const titleId = useId();
    const textId = useId();
    const cardRef = useRef<HTMLDivElement>(null);
    const returnFocus = useRef<HTMLElement | null>(null);
    // Matches the prerendered HTML on the first render, so hydration has
    // nothing to correct; the banner follows a moment later where needed.
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const visible =
        mounted &&
        !isPrerender() &&
        consentApplies() &&
        (settingsOpen || (choice === null && isPublicPath(pathname)));

    // Reopened from a link: straight to the settings with the current choice,
    // and focus moves in so keyboard users land where they asked to go.
    useEffect(() => {
        if (!settingsOpen) return;
        returnFocus.current = document.activeElement as HTMLElement | null;
        setDraft(choice ?? NO_CONSENT);
        setView("settings");
        requestAnimationFrame(() => cardRef.current?.focus());
    }, [settingsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!visible) return null;

    const decide = (next: ConsentChoice) => {
        save(next);
        setView("main");
        returnFocus.current?.focus();
    };

    // Back from the settings: to the banner while undecided, closed otherwise.
    const back = () => {
        if (choice === null) {
            setView("main");
            return;
        }
        closeSettings();
        setView("main");
        returnFocus.current?.focus();
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape" && view === "settings") back();
    };

    const rows: { category: ConsentCategory | "necessary" }[] = [
        { category: "necessary" },
        ...CONSENT_CATEGORIES.map((category) => ({ category })),
    ];

    return (
        <Card
            ref={cardRef}
            role="dialog"
            aria-labelledby={titleId}
            aria-describedby={textId}
            tabIndex={-1}
            onKeyDown={onKeyDown}
        >
            {view === "main" ? (
                <>
                    <Head>
                        <CookieDisc aria-hidden>
                            <MaterialIcon icon="cookie" size={20} />
                        </CookieDisc>
                        <Title id={titleId}>{t("components.cookie_banner.title")}</Title>
                    </Head>
                    <Text id={textId}>
                        <Trans
                            i18nKey="components.cookie_banner.body"
                            components={{ privacy: <Link to="/privacy-policy" /> }}
                        />
                    </Text>
                    <Actions>
                        <Button type="button" onClick={() => decide(ALL_CONSENT)}>
                            {t("components.cookie_banner.accept_all")}
                        </Button>
                        <OutlineButton type="button" onClick={() => decide(NO_CONSENT)}>
                            {t("components.cookie_banner.reject_all")}
                        </OutlineButton>
                        <CustomizeButton
                            type="button"
                            $muted
                            onClick={() => {
                                setDraft(choice ?? NO_CONSENT);
                                setView("settings");
                            }}
                        >
                            <span aria-hidden style={{ display: "inline-flex" }}>
                                <MaterialIcon icon="settings" size={18} />
                            </span>
                            {t("components.cookie_banner.customize")}
                        </CustomizeButton>
                    </Actions>
                </>
            ) : (
                <>
                    <Head>
                        <Title id={titleId}>{t("components.cookie_banner.settings_title")}</Title>
                        <CloseButton type="button" onClick={back} aria-label={t("components.cookie_banner.back")}>
                            <span aria-hidden style={{ display: "inline-flex" }}>
                                <MaterialIcon icon="close" size={20} />
                            </span>
                        </CloseButton>
                    </Head>
                    <Text id={textId}>{t("components.cookie_banner.settings_body")}</Text>
                    <Rows>
                        {rows.map(({ category }) => {
                            const locked = category === "necessary";
                            const on = locked || draft[category as ConsentCategory];
                            const labelId = `${titleId}-${category}`;
                            return (
                                <Row key={category}>
                                    <RowText>
                                        <b id={`${labelId}-title`}>{tStr(t, `components.cookie_banner.${category}_title`)}</b>
                                        <span id={`${labelId}-body`}>{tStr(t, `components.cookie_banner.${category}_body`)}</span>
                                    </RowText>
                                    <RowControl>
                                        {locked && <AlwaysOn>{t("components.cookie_banner.always_on")}</AlwaysOn>}
                                        <Switch
                                            type="button"
                                            role="switch"
                                            aria-checked={on}
                                            aria-labelledby={`${labelId}-title`}
                                            aria-describedby={`${labelId}-body`}
                                            $on={on}
                                            disabled={locked}
                                            onClick={() =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    [category]: !current[category as ConsentCategory],
                                                }))
                                            }
                                        />
                                    </RowControl>
                                </Row>
                            );
                        })}
                    </Rows>
                    <Actions>
                        <Button type="button" onClick={() => decide(ALL_CONSENT)}>
                            {t("components.cookie_banner.accept_all")}
                        </Button>
                        <OutlineButton type="button" $accent onClick={() => decide(draft)}>
                            {t("components.cookie_banner.save")}
                        </OutlineButton>
                        <OutlineButton type="button" onClick={() => decide(NO_CONSENT)}>
                            {t("components.cookie_banner.reject_all")}
                        </OutlineButton>
                    </Actions>
                </>
            )}
        </Card>
    );
};

export default CookieBanner;
